/**
 * Price Queries
 *
 * React Query hooks for Stripe Price CRUD via gobodo API.
 * Enabled only when REACT_APP_USE_GOBODO_API is true (prices are backend-only; no mock).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as stripePriceService from '../stripePriceService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import type { StripePriceRecord } from '../../types/index';
import type { CreatePricePayload, UpdatePricePayload } from '../stripePriceService';

/**
 * Get all prices
 */
export function usePrices() {
  return useQuery({
    queryKey: ['prices'],
    queryFn: () => stripePriceService.listPrices(),
    enabled: shouldUseGobodo(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get all prices for a product
 */
export function usePricesByProduct(productId: string) {
  return useQuery({
    queryKey: ['pricesByProduct', productId],
    queryFn: () => stripePriceService.listPricesByProduct(productId),
    enabled: !!productId && shouldUseGobodo(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get single price by ID
 */
export function usePrice(priceId: string) {
  return useQuery({
    queryKey: ['price', priceId],
    queryFn: () => stripePriceService.getPrice(priceId),
    enabled: !!priceId && shouldUseGobodo(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Create price mutation
 */
export function useCreatePrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreatePricePayload) => stripePriceService.createPrice(payload),
    onSuccess: (data: StripePriceRecord) => {
      queryClient.invalidateQueries({ queryKey: ['prices'] });
      queryClient.invalidateQueries({ queryKey: ['pricesByProduct', data.productId] });
    },
  });
}

/**
 * Update price mutation (only active and metadata are updateable per API)
 */
export function useUpdatePrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ priceId, updates }: { priceId: string; updates: UpdatePricePayload }) =>
      stripePriceService.updatePrice(priceId, updates),
    onSuccess: (data: StripePriceRecord) => {
      queryClient.invalidateQueries({ queryKey: ['prices'] });
      queryClient.invalidateQueries({ queryKey: ['price', data.id] });
      queryClient.invalidateQueries({ queryKey: ['pricesByProduct', data.productId] });
    },
  });
}

/**
 * Delete (archive) price mutation. Pass productId for cache invalidation.
 */
export function useDeletePrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ priceId, productId }: { priceId: string; productId: string }) =>
      stripePriceService.deletePrice(priceId).then(() => ({ priceId, productId })),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['prices'] });
      queryClient.invalidateQueries({ queryKey: ['price', variables.priceId] });
      queryClient.invalidateQueries({ queryKey: ['pricesByProduct', variables.productId] });
    },
  });
}
